import Table from "@components/vendor/dashboard/table";
import { TTableColumns } from "@components/vendor/dashboard/table/types";
import { ActionIcon } from "@mantine/core";
import RoutesInstance from "@routes/instances";
import { useMutation } from "@tanstack/react-query";
import Common from "@utilities/common";
import DateHelper from "@utilities/date";
import { FC } from "react";
import { BiEdit } from "react-icons/bi";
import { IoMdRemoveCircle } from "react-icons/io";
import {
    Link,
    useLocation,
    useNavigate,
    useSearchParams,
} from "react-router-dom";
import Filters from "./filters";
import classNames from "classnames";
import {
    MdOutlinePublishedWithChanges,
    MdOutlineUnpublished,
} from "react-icons/md";
import { useAuth } from "@contexts/auth";

interface Props {
    data: Array<IProducts["get"]>;
    pagination: Required<IReturnValue["pagination"]>;
    loading: boolean;
}

const ProductsContainer: FC<Props> = ({ data, loading, pagination }) => {
    const { pathname } = useLocation();

    const [URLSearchParams] = useSearchParams();

    const Navigate = useNavigate();

    const { profile } = useAuth();

    const {
        mutate: changePublishStatus,
        isLoading: isChangePublishStatusLoading,
    } = useMutation(
        ["publishProduct"],
        (id: number) =>
            Common.api.products.Publish({
                id: id,
            }),
        {
            onSuccess: () => {
                URLSearchParams.set("refetch", "true");

                Navigate(
                    {
                        pathname,
                        search: "?" + URLSearchParams.toString(),
                    },
                    {
                        replace: true,
                    },
                );
            },
        },
    );

    const handlePublishStatus = (id: number) => {
        changePublishStatus(id);
    };

    const { mutate, isLoading } = useMutation(
        ["removeProduct"],
        (id: number) =>
            Common.api.products.Delete({
                id: id,
            }),
        {
            onSuccess: () => {
                URLSearchParams.set("refetch", "true");

                Navigate(
                    {
                        pathname,
                        search: "?" + URLSearchParams.toString(),
                    },
                    {
                        replace: true,
                    },
                );
            },
        },
    );

    const handleRemove = (id: number) => {
        mutate(id);
    };

    const columns: TTableColumns<IProducts["get"]> = [
        {
            id: "faName",
            accessorKey: "نام فارسی",
            cell: (data) => (
                <div className="rtl:font-numeric">
                    {data.row.original.faName}
                </div>
            ),
        },
        {
            id: "enName",
            accessorKey: "نام انگلیسی",
            cell: (data) => (
                <div className="rtl:font-numeric">
                    {data.row.original.enName}
                </div>
            ),
        },
        {
            id: "categories",
            accessorKey: "دسته بندی ها",
            cell: (data) => (
                <div className="rtl:font-numeric">
                    {data.row.original.categories.map((item, i) =>
                        i > 0 ? ", " + item.faName : item.faName,
                    )}
                </div>
            ),
        },
        {
            id: "price",
            accessorKey: "قیمت",
            cell: (data) => (
                <div className="rtl:font-numeric">
                    {data.row.original.price}
                </div>
            ),
        },
        {
            id: "isPublish",
            accessorKey: "وضعیت",
            cell: (data) => (
                <div
                    className={classNames(
                        "rtl:font-numeric flex items-center gap-2",
                        data.row.original.isPublish
                            ? "text-green-500"
                            : "text-red-500",
                    )}>
                    {data.row.original.isPublish
                        ? "منتشر شده"
                        : "در انتظار انتشار"}

                    <ActionIcon
                        variant="subtle"
                        color={data.row.original.isPublish ? "red" : "green"}
                        radius={9999}
                        className="mx-1"
                        size="lg"
                        loading={isChangePublishStatusLoading}
                        disabled={isLoading || isChangePublishStatusLoading}
                        onClick={() =>
                            handlePublishStatus(data.row.original.id)
                        }>
                        {data.row.original.isPublish ? (
                            <MdOutlineUnpublished className="text-2xl" />
                        ) : (
                            <MdOutlinePublishedWithChanges className="text-2xl" />
                        )}
                    </ActionIcon>
                </div>
            ),
        },
        {
            id: "createDateTime",
            accessorKey: "زمان ساخت",
            cell: (data) => (
                <div className="rtl:font-numeric">
                    {DateHelper({
                        datetime: data.row.original.createDateTime,
                        format: "DD MMMM YYYY - HH:mm",
                    })}
                </div>
            ),
        },
        {
            accessorKey: "عملیات",
            cell: (data) => (
                <div className="flex items-center flex-wrap">
                    <Link
                        to={RoutesInstance.editProduct.replace(
                            ":id",
                            data.row.original.id.toString(),
                        )}>
                        <ActionIcon
                            variant="subtle"
                            color="yellow"
                            radius={9999}
                            className="mx-1"
                            size="lg"
                            disabled={isLoading}>
                            <BiEdit className="text-2xl" />
                        </ActionIcon>
                    </Link>

                    {profile.role !== "admin" && (
                        <ActionIcon
                            variant="subtle"
                            color="red"
                            radius={9999}
                            className="mx-1"
                            size="lg"
                            loading={isLoading}
                            disabled={isLoading}
                            onClick={() => handleRemove(data.row.original.id)}>
                            <IoMdRemoveCircle className="text-2xl" />
                        </ActionIcon>
                    )}
                </div>
            ),
        },
    ];

    return (
        <>
            <Filters />

            <Table<IProducts["get"]>
                columns={columns}
                data={data}
                loading={loading}
                options={{
                    rowNumber: true,
                }}
                pagination={{
                    activePage: pagination?.page || 0,
                    isStartFromZero: false,
                    lastPage: pagination
                        ? Math.ceil(pagination?.total / pagination?.limit)
                        : 1,
                    queryKey: "page",
                }}
            />
        </>
    );
};

export default ProductsContainer;
